import { CanvasPosition, Entry, EntityType, GridEntry, ReferenceEntry, RepositoryEntry } from '../types';
import type { GridAxisSnapFields } from './resolveGridAxisSnap';

/** Distance from the grid anchor to the first primary-axis reference card. */
export const GRID_PRIMARY_AXIS_ANCHOR_OFFSET = 280;
/** Spacing between primary-axis reference cards along the layout axis. */
export const GRID_PRIMARY_AXIS_ITEM_SPACING = 280;
export const GRID_SNAP_EPSILON = 1;

function compareRepositoryEntryRank(a: { rank?: string | number }, b: { rank?: string | number }): number {
	return String(a.rank ?? '').localeCompare(String(b.rank ?? ''), undefined, { numeric: true });
}

/** Positions for primary-axis references: columns → horizontal row; rows → vertical stack. */
export function computePrimaryAxisReferencePositions(args: {
	count: number;
	gridX: number;
	gridY: number;
	primaryLayoutVertical: boolean;
	anchorOffset?: number;
	itemSpacing?: number;
}): CanvasPosition[] {
	const {
		count,
		gridX,
		gridY,
		primaryLayoutVertical,
		anchorOffset = GRID_PRIMARY_AXIS_ANCHOR_OFFSET,
		itemSpacing = GRID_PRIMARY_AXIS_ITEM_SPACING,
	} = args;
	if (count <= 0) {
		return [];
	}
	return Array.from({ length: count }, (_, index) => {
		const step = anchorOffset + index * itemSpacing;
		if (primaryLayoutVertical) {
			return { x: gridX + step, y: gridY };
		}
		return { x: gridX, y: gridY + step };
	});
}

type GridPrimaryAxisReferenceSeed = {
	type: EntityType.Reference;
	parentId: string | null;
	repositoryId: string;
	repositoryEntryId: string;
	xy: CanvasPosition;
};

export function buildGridPrimaryAxisReferenceSeeds(args: {
	grid: GridEntry;
	gridPosition: CanvasPosition;
	repositoryEntryIds: string[];
	axisRepositoryId: string;
	parentId: string | null;
}): GridPrimaryAxisReferenceSeed[] {
	const { grid, gridPosition, repositoryEntryIds, axisRepositoryId, parentId } = args;
	const primaryLayoutVertical = (grid.direction ?? 'vertical') !== 'horizontal';
	const positions = computePrimaryAxisReferencePositions({
		count: repositoryEntryIds.length,
		gridX: gridPosition.x,
		gridY: gridPosition.y,
		primaryLayoutVertical,
	});
	return repositoryEntryIds.map((repositoryEntryId, index) => ({
		type: EntityType.Reference,
		parentId,
		repositoryId: axisRepositoryId,
		repositoryEntryId,
		xy: positions[index],
	}));
}

export function sortedRepositoryEntryIdsForAxisRepository(
	repositoryId: string,
	allRepositoryEntries: RepositoryEntry[],
): string[] {
	return allRepositoryEntries
		.filter((entry) => entry.repositoryId === repositoryId && entry.parentId === repositoryId)
		.sort(compareRepositoryEntryRank)
		.map((entry) => entry.id);
}

export function axisRepositoryEntryIdsMissingCanvasReferences(args: {
	axisRepositoryId: string;
	candidateRepositoryEntryIds: string[];
	visibleReferences: ReferenceEntry[];
}): string[] {
	const existing = new Set(
		args.visibleReferences
			.filter((reference) => reference.repositoryId === args.axisRepositoryId)
			.map((reference) => reference.repositoryEntryId),
	);
	return args.candidateRepositoryEntryIds.filter((id) => !existing.has(id));
}

type ProjectedSwimlaneLine = {
	coord: number;
	min: number;
	max: number;
};

export function buildAxisBounds({
	anchor,
	points,
	padding = 0,
}: {
	anchor?: number;
	points: number[];
	padding?: number;
}): { min: number; max: number } | null {
	const values = [...points];
	if (typeof anchor === 'number') {
		values.push(anchor);
	}
	if (!values.length) {
		return null;
	}
	return {
		min: Math.min(...values) - padding,
		max: Math.max(...values) + padding,
	};
}

export type GridSnapContext = {
	activeGridEntry: GridEntry | null;
	/** Target repository ids + swimlane flags derived from axis property ids (or legacy grid fields). */
	resolvedAxisSnap: GridAxisSnapFields | null;
	gridAxisBounds: { minX: number; maxX: number; minY: number; maxY: number } | null;
	gridSnapX?: number;
	gridSnapY?: number;
	projectedSwimlaneVertical?: ProjectedSwimlaneLine[];
	projectedSwimlaneHorizontal?: ProjectedSwimlaneLine[];
};

export function isGridSnapDisabled(
	activeGridEntry: GridEntry | null,
	selectedEntryIds: string[],
	groupSelectedIds?: string[]
): boolean {
	if (!activeGridEntry) {
		return false;
	}
	return selectedEntryIds.includes(activeGridEntry.id) || Boolean(groupSelectedIds?.includes(activeGridEntry.id));
}

export function resolveReferenceSnapMode(
	entry: Entry,
	position: CanvasPosition,
	context: GridSnapContext,
	snapThreshold: number
): 'vertical' | 'horizontal' | null {
	if (entry.type !== EntityType.Reference || !context.activeGridEntry || !context.gridAxisBounds) {
		return null;
	}
	const reference = entry as ReferenceEntry;
	const { resolvedAxisSnap, gridAxisBounds, gridSnapX, gridSnapY } = context;
	const axis = resolvedAxisSnap;
	if (
		typeof gridSnapX === 'number'
		&& axis?.yAxisRepositoryId
		&& reference.repositoryId === axis.yAxisRepositoryId
		&& position.y >= gridAxisBounds.minY
		&& position.y <= gridAxisBounds.maxY
		&& Math.abs(position.x - gridSnapX) <= snapThreshold
	) {
		return 'vertical';
	}
	if (
		typeof gridSnapY === 'number'
		&& axis?.xAxisRepositoryId
		&& reference.repositoryId === axis.xAxisRepositoryId
		&& position.x >= gridAxisBounds.minX
		&& position.x <= gridAxisBounds.maxX
		&& Math.abs(position.y - gridSnapY) <= snapThreshold
	) {
		return 'horizontal';
	}
	if (
		axis?.repositoryId
		&& reference.repositoryId === axis.repositoryId
		&& Array.isArray(context.projectedSwimlaneVertical)
		&& context.projectedSwimlaneVertical.some((lane) => (
			position.y >= lane.min
			&& position.y <= lane.max
			&& Math.abs(position.x - lane.coord) <= snapThreshold
		))
	) {
		return 'vertical';
	}
	if (
		axis?.repositoryId
		&& reference.repositoryId === axis.repositoryId
		&& Array.isArray(context.projectedSwimlaneHorizontal)
		&& context.projectedSwimlaneHorizontal.some((lane) => (
			position.x >= lane.min
			&& position.x <= lane.max
			&& Math.abs(position.y - lane.coord) <= snapThreshold
		))
	) {
		return 'horizontal';
	}
	return null;
}

export function applyReferenceSnap(
	entry: Entry,
	position: CanvasPosition,
	context: GridSnapContext,
	snapThreshold: number
): CanvasPosition {
	const snapMode = resolveReferenceSnapMode(entry, position, context, snapThreshold);
	if (snapMode === 'vertical' && typeof context.gridSnapX === 'number') {
		if (
			entry.type === EntityType.Reference
			&& context.resolvedAxisSnap?.repositoryId
			&& (entry as ReferenceEntry).repositoryId === context.resolvedAxisSnap.repositoryId
			&& Array.isArray(context.projectedSwimlaneVertical)
			&& context.projectedSwimlaneVertical.length
		) {
			const eligibleLanes = context.projectedSwimlaneVertical.filter((lane) => (
				position.y >= lane.min && position.y <= lane.max
			));
			if (eligibleLanes.length) {
				const closestLane = eligibleLanes.reduce((closest, lane) => (
					Math.abs(position.x - lane.coord) < Math.abs(position.x - closest.coord) ? lane : closest
				), eligibleLanes[0]);
				return { x: closestLane.coord, y: position.y };
			}
		}
		return { x: context.gridSnapX, y: position.y };
	}
	if (snapMode === 'horizontal' && typeof context.gridSnapY === 'number') {
		if (
			entry.type === EntityType.Reference
			&& context.resolvedAxisSnap?.repositoryId
			&& (entry as ReferenceEntry).repositoryId === context.resolvedAxisSnap.repositoryId
			&& Array.isArray(context.projectedSwimlaneHorizontal)
			&& context.projectedSwimlaneHorizontal.length
		) {
			const eligibleLanes = context.projectedSwimlaneHorizontal.filter((lane) => (
				position.x >= lane.min && position.x <= lane.max
			));
			if (eligibleLanes.length) {
				const closestLane = eligibleLanes.reduce((closest, lane) => (
					Math.abs(position.y - lane.coord) < Math.abs(position.y - closest.coord) ? lane : closest
				), eligibleLanes[0]);
				return { x: position.x, y: closestLane.coord };
			}
		}
		return { x: position.x, y: context.gridSnapY };
	}
	return position;
}

/** Canvas entry with layout position (same rules as `getPosition` on the canvas). */
export type CanvasEntryLayoutRow = {
	entry: Entry;
	x: number;
	y: number;
};

/** Reference cards snapped to either axis of the grid at `gridAnchor`. */
export function computeSnappedReferenceIdsForGrid(
	gridSnap: GridAxisSnapFields,
	gridSnapX: number,
	gridSnapY: number,
	entriesWithPos: CanvasEntryLayoutRow[],
	epsilon: number,
): string[] {
	return entriesWithPos
		.filter(({ entry, x, y }) => {
			if (entry.type !== EntityType.Reference) {
				return false;
			}
			const repositoryId = (entry as ReferenceEntry).repositoryId;
			const snappedToVerticalAxis = Boolean(gridSnap.yAxisRepositoryId)
				&& repositoryId === gridSnap.yAxisRepositoryId
				&& Math.abs(x - gridSnapX) <= epsilon;
			const snappedToHorizontalAxis = Boolean(gridSnap.xAxisRepositoryId)
				&& repositoryId === gridSnap.xAxisRepositoryId
				&& Math.abs(y - gridSnapY) <= epsilon;
			return snappedToVerticalAxis || snappedToHorizontalAxis;
		})
		.map(({ entry }) => entry.id);
}

type AxisRoot = { id: string; x: number; y: number };

/**
 * For each swimlane root on the grid, ids of collection references in that lane (static bounds, no drag padding).
 */
export function computeSwimlaneChildIdsByRootForGrid(
	gridSnap: GridAxisSnapFields,
	gridSnapX: number,
	gridSnapY: number,
	entriesWithPos: CanvasEntryLayoutRow[],
	epsilon: number,
): Map<string, string[]> {
	const positionedReferences = entriesWithPos.filter(({ entry }) => entry.type === EntityType.Reference);
	const xAxisRoots: AxisRoot[] = positionedReferences
		.filter(({ entry, y }) => (
			Boolean(gridSnap.xAxisRepositoryId)
			&& (entry as ReferenceEntry).repositoryId === gridSnap.xAxisRepositoryId
			&& Math.abs(y - gridSnapY) <= epsilon
		))
		.map(({ entry, x, y }) => ({ id: entry.id, x, y }));
	const yAxisRoots: AxisRoot[] = positionedReferences
		.filter(({ entry, x }) => (
			Boolean(gridSnap.yAxisRepositoryId)
			&& (entry as ReferenceEntry).repositoryId === gridSnap.yAxisRepositoryId
			&& Math.abs(x - gridSnapX) <= epsilon
		))
		.map(({ entry, x, y }) => ({ id: entry.id, x, y }));

	const collectionReferencesWithPosition = positionedReferences
		.filter(({ entry }) => (
			Boolean(gridSnap.repositoryId)
			&& (entry as ReferenceEntry).repositoryId === gridSnap.repositoryId
		))
		.map(({ entry, x, y }) => ({ id: entry.id, x, y }));

	const projectedSwimlaneVertical = gridSnap.xAxisSwimlaneEnabled
		? xAxisRoots.map((root) => {
			const laneReferences = collectionReferencesWithPosition.filter((reference) => (
				Math.abs(reference.x - root.x) <= epsilon
			));
			const yBounds = buildAxisBounds({
				anchor: root.y,
				points: laneReferences.map((reference) => reference.y),
				padding: 0,
			});
			return {
				rootId: root.id,
				coord: root.x,
				min: yBounds?.min ?? root.y,
				max: yBounds?.max ?? root.y,
			};
		})
		: [];

	const projectedSwimlaneHorizontal = gridSnap.yAxisSwimlaneEnabled
		? yAxisRoots.map((root) => {
			const laneReferences = collectionReferencesWithPosition.filter((reference) => (
				Math.abs(reference.y - root.y) <= epsilon
			));
			const xBounds = buildAxisBounds({
				anchor: root.x,
				points: laneReferences.map((reference) => reference.x),
				padding: 0,
			});
			return {
				rootId: root.id,
				coord: root.y,
				min: xBounds?.min ?? root.x,
				max: xBounds?.max ?? root.x,
			};
		})
		: [];

	const byRoot = new Map<string, Set<string>>();
	for (const lane of projectedSwimlaneVertical) {
		const childIds = collectionReferencesWithPosition
			.filter((reference) => (
				reference.y >= lane.min
				&& reference.y <= lane.max
				&& Math.abs(reference.x - lane.coord) <= epsilon
			))
			.map((reference) => reference.id);
		if (!byRoot.has(lane.rootId)) {
			byRoot.set(lane.rootId, new Set<string>());
		}
		childIds.forEach((id) => byRoot.get(lane.rootId)?.add(id));
	}
	for (const lane of projectedSwimlaneHorizontal) {
		const childIds = collectionReferencesWithPosition
			.filter((reference) => (
				reference.x >= lane.min
				&& reference.x <= lane.max
				&& Math.abs(reference.y - lane.coord) <= epsilon
			))
			.map((reference) => reference.id);
		if (!byRoot.has(lane.rootId)) {
			byRoot.set(lane.rootId, new Set<string>());
		}
		childIds.forEach((id) => byRoot.get(lane.rootId)?.add(id));
	}
	return new Map(Array.from(byRoot.entries()).map(([id, set]) => [id, Array.from(set)]));
}

/**
 * Starting from `seedIds`, repeatedly add swimlane children for any id that is a lane root until fixpoint.
 */
export function expandIdsBySwimlaneRoots(
	seedIds: string[],
	swimlaneChildIdsByRootId: ReadonlyMap<string, readonly string[]>,
): string[] {
	const out = new Set(seedIds);
	let growth = true;
	while (growth) {
		growth = false;
		for (const id of Array.from(out)) {
			for (const childId of swimlaneChildIdsByRootId.get(id) ?? []) {
				if (!out.has(childId)) {
					out.add(childId);
					growth = true;
				}
			}
		}
	}
	return Array.from(out);
}

/**
 * When the grid uses swimlanes on the main axis, returns the canvas reference id of the lane header
 * whose band contains `position`, or null if none match.
 */
export function findSwimlaneLaneRootIdForCollectionPosition(
	position: CanvasPosition,
	gridDirection: 'vertical' | 'horizontal',
	projectedSwimlaneVertical: ReadonlyArray<{ rootId: string; coord: number; min: number; max: number }>,
	projectedSwimlaneHorizontal: ReadonlyArray<{ rootId: string; coord: number; min: number; max: number }>,
	snapThreshold: number,
): string | null {
	if (gridDirection === 'vertical') {
		for (const lane of projectedSwimlaneVertical) {
			if (
				position.y >= lane.min
				&& position.y <= lane.max
				&& Math.abs(position.x - lane.coord) <= snapThreshold
			) {
				return lane.rootId;
			}
		}
		return null;
	}
	for (const lane of projectedSwimlaneHorizontal) {
		if (
			position.x >= lane.min
			&& position.x <= lane.max
			&& Math.abs(position.y - lane.coord) <= snapThreshold
		) {
			return lane.rootId;
		}
	}
	return null;
}

/** Axis-snapped references plus all collection entries in their swimlanes (recursive over roots). */
export function computeEntriesLinkedToGridAtAnchor(
	gridSnap: GridAxisSnapFields,
	gridAnchor: { x: number; y: number },
	entriesWithPos: CanvasEntryLayoutRow[],
	epsilon: number,
): string[] {
	const swimlaneMap = computeSwimlaneChildIdsByRootForGrid(
		gridSnap,
		gridAnchor.x,
		gridAnchor.y,
		entriesWithPos,
		epsilon,
	);
	const mainAxisIds = computeSnappedReferenceIdsForGrid(
		gridSnap,
		gridAnchor.x,
		gridAnchor.y,
		entriesWithPos,
		epsilon,
	);
	return expandIdsBySwimlaneRoots(mainAxisIds, swimlaneMap);
}
